import {lazy, Suspense, useEffect, useState} from 'react'
import {App as AntApp, ConfigProvider} from "antd";
import {Route, Routes, useLocation} from "react-router";
import LoadingScreen from "@/components/navigation/loadingScreen.tsx";
import {useThemeManager} from "@/utils/useThemeManager.ts";
import MainShell from "@/components/mainShell.tsx";

import {restoreSessionAsync, useAppDispatch, useAppSelector} from "@/store";
import EditVehicleScreen from "@/screens/editVehicle.tsx";
import {supabase} from "@/utils/supabase.ts";
import useGlassTheme from "@/glassTheme.ts";
import {keysToCamelCase} from "@/utils";


const HomeScreen = lazy(() => import("./screens/home.tsx"));
const ListingsScreen = lazy(() => import("./screens/listings.tsx"));
const AuctionsScreen = lazy(() => import('./screens/auctions.tsx'));
const CreateScreen = lazy(() => import("./screens/create.tsx"));
const SettingsScreen = lazy(() => import("./screens/settings.tsx"));
const MessagesScreen = lazy(() => import("@/screens/messages.tsx"));
const SingleListingScreen = lazy(() => import("@/screens/singleListing.tsx"));
const SingleAuctionScreen = lazy(() => import("@/screens/singleAuction.tsx"));
const EditListingScreen = lazy(() => import("@/screens/editListing.tsx"));
const SignupScreen = lazy(() => import("@/screens/authentication/signupScreen.tsx"));
const LoginScreen = lazy(() => import("@/screens/authentication/loginScreen.tsx"));
const DealerWizard = lazy(() => import("@/screens/dealerWizard.tsx"));
const ForgotPasswordScreen = lazy(() => import("@/screens/authentication/forgotPassword.tsx"));
const ResetPasswordScreen = lazy(() => import("@/screens/authentication/resetPassword.tsx"));
const VehicleScreen = lazy(() => import("@/screens/vehicle.tsx"));

type DealerNotificationRecord = {
    id: string;
    dealerId: string;
    type: string;
    title: string;
    body?: string;
    conversationId?: string;
    vehicleId?: string;
    listingId?: string;
    auctionId?: string;
    metadata?: Record<string, unknown>;
    createdAt: string;
};

function DealerNotificationBridge() {
    const {notification} = AntApp.useApp();
    const dealerId = useAppSelector((state) => state.authentication.user?.id);
    const currentConversationId = useAppSelector((state) => state.messages.currentConversation?.id);
    const location = useLocation();

    useEffect(() => {
        if (!dealerId) {
            return;
        }

        const channel = supabase
            .channel(`dealer-notifications:${dealerId}`)
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "dealer_notifications",
                    filter: `dealer_id=eq.${dealerId}`
                },
                (payload) => {
                    const record = keysToCamelCase<DealerNotificationRecord>(payload.new);
                    if (!record?.id) {
                        return;
                    }

                    if (
                        record.type === "customer_message"
                        && location.pathname === "/messages"
                        && currentConversationId
                        && currentConversationId === record.conversationId
                        && !document.hidden
                    ) {
                        return;
                    }

                    notification.info({
                        title: record.title,
                        description: record.body || "Dealer notification",
                        placement: "topRight"
                    });
                }
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [currentConversationId, dealerId, location.pathname, notification]);

    return null;
}

function App() {
    const currentTheme = useThemeManager();
    const configTheme = useGlassTheme(currentTheme);
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);
    const [bootstrapping, setBootstrapping] = useState(true);

    useEffect(() => {
        void dispatch(restoreSessionAsync()).finally(() => setBootstrapping(false));
    }, [dispatch]);

    useEffect(() => {
        setLoading(true);
        const id = setTimeout(() => setLoading(false), 50); // small delay to show loader
        return () => clearTimeout(id);
    }, [location.pathname]);

    if (loading || bootstrapping) return <LoadingScreen/>;
    // if (true) return <LoadingScreen/>;
    return (
        <ConfigProvider
            theme={configTheme}>
            <AntApp>
                <DealerNotificationBridge/>
                <div className="app-viewport bg-light-bg transition-colors dark:bg-dark-bg dark:text-white">

                    <Routes>
                        <Route element={<MainShell/>}>
                            {/* Public */}
                            <Route
                                path="/"
                                element={
                                    <Suspense fallback={<LoadingScreen/>}>
                                        <HomeScreen/>
                                    </Suspense>
                                }
                            />
                            <Route path={'/vehicle/:id'}
                                   element={<Suspense fallback={<LoadingScreen/>}>
                                       <VehicleScreen/>
                            </Suspense>}/>
                            <Route path={'/vehicle/:id/edit'}
                                   element={<Suspense fallback={<LoadingScreen/>}>
                                       <EditVehicleScreen/>
                            </Suspense>}/>
                            <Route path={'/listings'} element={<Suspense fallback={<LoadingScreen/>}>
                                <ListingsScreen/>
                            </Suspense>}/>
                            <Route path={'/listing/:id'} element={<Suspense fallback={<LoadingScreen/>}>
                                <SingleListingScreen/>
                            </Suspense>}/>
                            <Route path={'/listing/:id/edit'} element={<Suspense fallback={<LoadingScreen/>}>
                                <EditListingScreen/>
                            </Suspense>}/>
                            <Route path={'/auctions'} element={<Suspense fallback={<LoadingScreen/>}>
                                <AuctionsScreen/>
                            </Suspense>}/>
                            <Route path={'/auction/:id'} element={<Suspense fallback={<LoadingScreen/>}>
                                <SingleAuctionScreen/>
                            </Suspense>}/>
                            <Route path={'/messages'} element={<Suspense fallback={<LoadingScreen/>}>
                                <MessagesScreen/>
                            </Suspense>}/>
                            <Route path={'/create'} element={<Suspense fallback={<LoadingScreen/>}>
                                <CreateScreen/>
                            </Suspense>}/>
                            <Route path={'/settings'} element={<Suspense fallback={<LoadingScreen/>}>
                                <SettingsScreen/>
                            </Suspense>}/>


                        </Route>
                        <Route path={'/wizard'}
                               element={<Suspense fallback={<LoadingScreen/>}><DealerWizard/></Suspense>}/>
                        <Route path={'/login'}
                               element={<Suspense fallback={<LoadingScreen/>}><LoginScreen/></Suspense>}/>
                        <Route path={'/forgot-password'}
                               element={<Suspense fallback={<LoadingScreen/>}><ForgotPasswordScreen/></Suspense>}/>
                        <Route path={'/reset-password'}
                               element={<Suspense fallback={<LoadingScreen/>}><ResetPasswordScreen/></Suspense>}/>
                        <Route path={'/sign-up'}
                               element={<Suspense fallback={<LoadingScreen/>}><SignupScreen/></Suspense>}/>
                    </Routes>
                </div>

            </AntApp>
        </ConfigProvider>
    )
}

export default App
